import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { Navigation, Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Modal from '../Modal/Modal';
import CrossMark from 'components/Icons/CrossMark';
import 'swiper/css';
import 'swiper/css/navigation';
import UIModal from '../Others/UIModal';

interface InstagramVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialVideoIndex?: number;
  videos: {
    id: string;
    videoUrl: string;
    thumbnailUrl: string;
    title?: string;
  }[];
}

export default function InstagramVideoModal({
  isOpen,
  onClose,
  initialVideoIndex = 0,
  videos
}: InstagramVideoModalProps) {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(initialVideoIndex);
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);
  const [mobileSwiperInstance, setMobileSwiperInstance] = useState<SwiperInstance | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentVideo = videos[currentVideoIndex];

  // Calculate responsive slides per view
  const getSlidesPerView = () => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 768) return Math.min(5, videos.length); // Mobile
      return Math.min(6, videos.length); // Desktop and tablet
    }
    return Math.min(6, videos.length); // Fallback
  };

  const [slidesPerView, setSlidesPerView] = useState(getSlidesPerView());

  // Update slides per view on window resize
  useEffect(() => {
    const handleResize = () => {
      setSlidesPerView(getSlidesPerView());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [videos.length]);

  // Update current video index when modal opens with initialVideoIndex
  useEffect(() => {
    if (isOpen) {
      setCurrentVideoIndex(initialVideoIndex);
    }
  }, [isOpen, initialVideoIndex]);

  const handleThumbnailClick = (index: number) => {
    setCurrentVideoIndex(index);

    // Auto-slide thumbnails to center the selected one
    if (videos.length > slidesPerView) {
      // Calculate which slide to go to for optimal viewing
      let targetSlide = Math.max(0, index - Math.floor(slidesPerView / 2));

      // Ensure we don't go beyond the last possible slide
      const maxSlide = videos.length - slidesPerView;
      targetSlide = Math.min(targetSlide, maxSlide);

      // Slide both desktop and mobile swipers
      if (swiperInstance) {
        swiperInstance.slideTo(targetSlide);
      }
      if (mobileSwiperInstance) {
        mobileSwiperInstance.slideTo(targetSlide);
      }
    }
  };

  const nextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      handleThumbnailClick(currentVideoIndex + 1);
    }
  };

  const prevVideo = () => {
    if (currentVideoIndex > 0) {
      handleThumbnailClick(currentVideoIndex - 1);
    }
  };

  if (!isOpen || !currentVideo) return null;

  return (
    <UIModal
      isVisible={isOpen}
      onToggleVisibility={onClose}
      modalCardClassName="relative w-[450px] overflow-hidden 2xl:mt-[120px] mdx:my-auto mdx:h-auto mdx:w-full mdx:max-w-[95vw] "
    >
      <div>
        {/* Desktop Modal */}
        <div className="bg-white rounded-[10px] shadow-xl overflow-hidden relative">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 gap-5">
            <div className="w-[18px] h-[18px]"></div>
            <h2 className="text-[20px] font-semibold leading-[1.1] tracking-[-0.01em] text-[#2A2A2A] text-center">
              Customer Testimonials
            </h2>
            <div className="w-[18px] h-[18px]"></div>
          </div>

          {/* Main content */}
          <div className="px-6 pb-6">
            {/* Main video area */}
            <div className="relative w-[100%] md:h-[700px] rounded-[12px] overflow-hidden mb-6">
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                poster={currentVideo.thumbnailUrl}
                controls
                controlsList="nodownload"
                playsInline
                autoPlay
                key={currentVideo.id}
              >
                <source src={currentVideo.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {/* Navigation arrows for multiple videos */}
              {videos.length > 1 && (
                <>
                  {currentVideoIndex > 0 && (
                    <button
                      onClick={prevVideo}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 w-[32px] h-[32px] bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all z-10"
                    >
                      <svg width="11" height="17" viewBox="0 0 11 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.93047 8.49902L0.931446 8.5L0.93047 8.50098L8.92949 16.5L10.2 15.2295L3.47051 8.5L10.2 1.77051L8.92949 0.5L0.93047 8.49902Z" fill="#616161" />
                      </svg>
                    </button>
                  )}

                  {currentVideoIndex < videos.length - 1 && (
                    <button
                      onClick={nextVideo}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-[32px] h-[32px] bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all z-10"
                    >
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.26953 8.49902L9.26855 8.5L9.26953 8.50098L1.27051 16.5L0 15.2295L6.72949 8.5L0 1.77051L1.27051 0.5L9.26953 8.49902Z" fill="#616161" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Thumbnail navigation with Swiper - Responsive */}
            {videos.length > 1 && (
              <div className="w-full max-w-[400px] mx-auto relative">
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={8}
                  slidesPerView={slidesPerView}
                  slidesPerGroup={1}
                  watchSlidesProgress={true}
                  navigation={{
                    nextEl: '.swiper-button-next-thumb',
                    prevEl: '.swiper-button-prev-thumb'
                  }}
                  onSwiper={setSwiperInstance}
                  className="thumbnail-swiper"
                >
                  {videos.map((video, index) => (
                    <SwiperSlide key={video.id}>
                      <button
                        onClick={() => handleThumbnailClick(index)}
                        className={cx(
                          "w-[60px] h-[60px] rounded-[12px] overflow-hidden border-2 transition-all",
                          {
                            "border-[#2A2A2A]": currentVideoIndex === index,
                            "border-transparent opacity-60 hover:opacity-80": currentVideoIndex !== index
                          }
                        )}
                      >
                        <Image
                          src={video.thumbnailUrl}
                          alt={`Testimonial ${index + 1}`}
                          width={60}
                          height={60}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Navigation buttons for thumbnails - only show if more videos than visible */}
                {/* {videos.length > slidesPerView && (
                  <>
                    <button className="swiper-button-prev-thumb absolute left-[-16px] top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center opacity-80 hover:opacity-100 transition-all z-10 ">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="#616161" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button className="swiper-button-next-thumb absolute right-[-16px] top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center opacity-80 hover:opacity-100 transition-all z-10 ">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="#616161" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </>
                )} */}
              </div>
            )}
          </div>
        </div>
      </div>
    </UIModal>
  );
}